import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useLanguageStore, t } from '../stores/languageStore';
import { roomApi, roomMessageApi } from '../utils/roomApi';
import { Room, RoomMessage } from '../utils/roomApi';
import { Send, ArrowLeft, Clock, Smile, Image } from 'lucide-react';
import RoomPaymentModal from '../components/RoomPaymentModal';
import EmojiPicker from '../components/EmojiPicker';

const RoomChatPage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const { user, isAuthenticated } = useAuthStore();
  const { language } = useLanguageStore();
  const [room, setRoom] = useState<Room | null>(null);
  const [messages, setMessages] = useState<RoomMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isMember, setIsMember] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [sendingIds, setSendingIds] = useState<Set<number>>(new Set());
  const [justSent, setJustSent] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const handlePaymentSuccess = () => {
    window.location.reload();
  };

  useEffect(() => {
    if (!roomId || !isAuthenticated) {
      if (!isAuthenticated) {
        navigate('/login');
      }
      return;
    }

    const initRoom = async () => {
      try {
        setLoading(true);
        
        const [roomResponse, memberResponse] = await Promise.all([
          roomApi.getRoom(parseInt(roomId)),
          roomApi.checkMembership(parseInt(roomId))
        ]);

        if (roomResponse.success && roomResponse.data) {
          setRoom(roomResponse.data);
        }

        if (memberResponse.success && memberResponse.data) {
          setIsMember(memberResponse.data.is_member);
          if (memberResponse.data.is_member) {
            await fetchMessages();
          }
        }
      } catch (error) {
        console.error('初始化房间失败:', error);
      } finally {
        setLoading(false);
      }
    };

    initRoom();
  }, [roomId, isAuthenticated, navigate]);

  useEffect(() => {
    if (isMember && roomId) {
      const interval = setInterval(() => {
        fetchMessages();
      }, 1500);

      return () => clearInterval(interval);
    }
  }, [isMember, roomId]);

  const fetchMessages = async () => {
    try {
      const response = await roomMessageApi.getMessages(parseInt(roomId!));
      if (response.success && response.messages) {
        setMessages(response.messages);
      }
    } catch (error) {
      console.error('获取消息失败:', error);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() && !selectedImage) return;

    const content = newMessage.trim() || '[图片]';
    const imageData = selectedImage;
    
    // 创建临时消息（乐观更新）
    const tempId = -Date.now();
    const tempMessage: RoomMessage = {
      id: tempId,
      room_id: parseInt(roomId!),
      user_id: user!.id,
      user: user ? {
        id: user.id,
        username: user.username,
        nickname: user.nickname || null,
        avatar: user.avatar || null,
        role: user.role
      } : undefined,
      content: content,
      image_url: selectedImage || undefined,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    setMessages(prev => [...prev, tempMessage]);
    setSendingIds(prev => new Set([...prev, tempId]));
    setNewMessage('');
    setSelectedImage(null);
    setJustSent(true);

    try {
      const response = await roomMessageApi.sendMessage(
        parseInt(roomId!),
        content,
        imageData
      );
      
      if (response.success) {
        await fetchMessages();
        setSendingIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(tempId);
          return newSet;
        });
      } else {
        // 发送失败，移除临时消息
        setMessages(prev => prev.filter(msg => msg.id !== tempId));
        setSendingIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(tempId);
          return newSet;
        });
        alert(response.message || '发送失败');
      }
    } catch (error: any) {
      // 发送失败，移除临时消息
      setMessages(prev => prev.filter(msg => msg.id !== tempId));
      setSendingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(tempId);
        return newSet;
      });
      console.error('发送消息失败:', error);
      const errorMessage = error.response?.data?.message || error.message || '发送消息失败';
      alert(errorMessage);
    }
  };

  const handleEmojiClick = (emoji: string) => {
    setNewMessage((prev) => prev + emoji);
    inputRef.current?.focus();
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result as string;
      if (result) {
        setSelectedImage(result);
        inputRef.current?.focus();
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          const reader = new FileReader();
          reader.onload = (event) => {
            const result = event.target?.result as string;
            if (result) {
              setSelectedImage(result);
            }
          };
          reader.readAsDataURL(file);
          e.preventDefault();
          break;
        }
      }
    }
  };

  const [isAtBottom, setIsAtBottom] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      setIsAtBottom(distanceFromBottom < 50);
    }
  };

  useEffect(() => {
    if (!loading) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  useEffect(() => {
    if (isAtBottom || justSent) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setJustSent(false);
    }
  }, [messages, isAtBottom, justSent]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!room) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{t('room.notMember').split('{roomName}')[0]}</p>
        <button onClick={() => navigate('/rooms')} className="btn btn-primary mt-4">
          {t('room.joinRoomTip')}
        </button>
      </div>
    );
  }

  if (!isMember) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/rooms')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          {t('room.joinRoomTip')}
        </button>
        <div className="card p-8 text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('room.notMember').replace('{roomName}', room.name)}</h2>
          <p className="text-gray-600 mb-6">{t('room.purchaseToJoin')}</p>
          <button
            onClick={() => setShowPaymentModal(true)}
            className="btn btn-primary"
          >
            {t('room.purchase')}
          </button>
        </div>

        <RoomPaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={handlePaymentSuccess}
          roomName={room?.name || ''}
          amount={Number(room?.price) || 0}
          priceType={room?.price_type || 'monthly'}
          roomId={room?.id || 0}
          trc20Address={room?.trc20_address}
          erc20Address={room?.erc20_address}
        />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/rooms')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          {t('room.joinRoomTip')}
        </button>
        <div className="text-right">
          <h1 className="text-2xl font-bold text-gray-900">{room.name}</h1>
          <p className="text-gray-500 text-sm">{room.description}</p>
        </div>
      </div>

      <div className="card h-[500px] flex flex-col">
        <div 
          ref={containerRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto p-4 space-y-4" 
          id="messages-container">
          {messages.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {t('room.firstMessage')}
            </div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                className={`flex items-start space-x-3 ${
                  message.user_id === user?.id ? 'flex-row-reverse' : ''
                }`}
              >
                <img
                  src={message.user?.avatar || `https://ui-avatars.com/api/?name=${message.user?.nickname || message.user?.username || '?'}&background=3b82f6&color=fff`}
                  alt={message.user?.nickname || message.user?.username || 'User'}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div className={`max-w-xs sm:max-w-md ${
                  message.user_id === user?.id ? 'text-right' : ''
                }`}>
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="font-medium text-gray-900">
                      {message.user?.nickname || message.user?.username}
                    </span>
                    <span className="text-xs text-gray-400 flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      {new Date(message.created_at).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div
                    className={`px-4 py-2 rounded-lg ${
                      message.user_id === user?.id
                        ? 'bg-primary-500 text-white rounded-br-sm'
                        : 'bg-gray-100 text-gray-900 rounded-bl-sm'
                    }`}
                  >
                    {message.image_url ? (
                      <img
                        src={message.image_url}
                        alt={t('chat.imageMessage')}
                        className="max-w-xs rounded-lg"
                        onClick={() => window.open(message.image_url, '_blank')}
                        style={{ cursor: 'pointer' }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 24 24" fill="none" stroke="%239ca3af" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"%3E%3Crect x="3" y="3" width="18" height="18" rx="2" ry="2"%3E%3C/rect%3E%3Ccircle cx="8.5" cy="8.5" r="1.5"%3E%3C/circle%3E%3Cpolyline points="21 15 16 10 5 21"%3E%3C/polyline%3E%3C/svg%3E';
                          target.style.opacity = '0.5';
                        }}
                      />
                    ) : (
                      <span className="whitespace-pre-wrap">{message.content}</span>
                    )}
                    {sendingIds.has(message.id) && (
                      <span className="ml-2 inline-block animate-pulse text-xs opacity-80">{t('room.sending')}</span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200">
          {selectedImage && (
            <div className="mb-3 relative inline-block">
              <img
                src={selectedImage}
                alt={t('chat.preview')}
                className="max-h-32 rounded-lg border border-gray-200"
              />
              <button
                type="button"
                onClick={() => setSelectedImage(null)}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
              >
                ×
              </button>
            </div>
          )}
          <div className="flex space-x-3">
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowEmojiPicker(true)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={!isMember}
              >
                <Smile className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="relative">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={!isMember}
              >
                <Image className="w-5 h-5 text-gray-600" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>

            <input
              ref={inputRef}
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onPaste={handlePaste}
              placeholder={t('chat.placeholder')}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              disabled={!isMember}
            />
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!newMessage.trim() && !selectedImage || !isMember}
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </form>

        {showEmojiPicker && (
          <EmojiPicker
            onSelect={handleEmojiClick}
            onClose={() => setShowEmojiPicker(false)}
          />
        )}
      </div>
    </div>
  );
};

export default RoomChatPage;